import { type ReactNode, useEffect, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Combobox, DropdownButton } from "@similarweb/ui-components";

import type { ChipGroupItem } from "app/pages/sales-intelligence/common-components/filters-panel/components/ChipsGroup/ChipsGroup";
import ChipsGroup from "app/pages/sales-intelligence/common-components/filters-panel/components/ChipsGroup/ChipsGroup";
import { FilterConditionDialog } from "app/pages/sales-intelligence/common-components/filters-panel/components/FilterConditionDialog/FilterConditionDialog";
import { FormFieldLabel } from "app/pages/sales-intelligence/common-components/form-fields";
import { StyledBaseDropdownButtonWrap } from "app/pages/sales-intelligence/common-components/styles";
import type { DynamicFilterGroupValue } from "app/pages/sales-intelligence/domain/search/types/dynamic-filter-values";
import type { InclusionOption } from "app/pages/sales-intelligence/types/commonMaps";
import { inclusionOptionMap, operatorOptionMap } from "app/pages/sales-intelligence/types/commonMaps";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import type { DynamicGroupFilter } from "../../../models/DynamicGroupFilter";
import { createDynamicGroupFilterViewModel } from "../../../models/view/dynamic-group-filter/factory";
import advancedFilteringI18nKeys from "../../../utils/i18n";
import InclusionGroup from "../../atoms/InclusionGroup/InclusionGroup";

import { GroupField } from "./GroupField";
import { StyledDialogContent } from "./styles";

type ConditionModalProps = {
    readonly isOpen: boolean;
    readonly filter: DynamicGroupFilter;
    /** The committed group value the local draft is seeded from on each open. */
    readonly value: DynamicFilterGroupValue;
    /** Member key to preselect — set when editing an existing condition, omitted when adding a new one. */
    readonly initialFieldKey?: string;
    readonly onApply: (next?: DynamicFilterGroupValue) => void;
    readonly onClose: VoidFunction;
};

export const ConditionModal = ({ isOpen, filter, value, initialFieldKey, onApply, onClose }: ConditionModalProps) => {
    const { translate } = useTranslate();

    const vm = useMemo(() => createDynamicGroupFilterViewModel({ filter, value }), [filter, value]);

    const [draft, setDraft] = useState<DynamicFilterGroupValue>(value);
    const [selectedBaseKey, setSelectedBaseKey] = useState<string | undefined>(undefined);
    const [inclusion, setInclusion] = useState<InclusionOption>(inclusionOptionMap.includeOnly);
    const [isPickerOpen, setIsPickerOpen] = useState(false);

    const activeMember = vm.resolveActiveMember(selectedBaseKey, inclusion);

    // Seed the draft with only the condition this session authors — edit pre-fills the clicked condition's value
    // so its chips show; add starts empty so a newly picked field begins as a fresh condition (never the existing
    // one), even on the same inclusion side.
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const seeded = vm.seedDraft(initialFieldKey);

        setDraft(seeded.draft);
        setSelectedBaseKey(seeded.selectedBaseKey);
        setInclusion(seeded.inclusion);
        setIsPickerOpen(false);
    }, [vm, isOpen, initialFieldKey]);

    const updateDraft = (next?: DynamicFilterGroupValue): void => setDraft(next ?? {});

    const selectField = (key: string): void => {
        setSelectedBaseKey(key);
        setIsPickerOpen(false);
    };

    const chipItems: ChipGroupItem[] = vm
        .chipItems(activeMember?.key, draft)
        .map((chip) => ({ name: chip.label, type: chip.id }));

    const disabledOptionIds = vm.disabledOptionIds({
        selectedBaseKey,
        activeMemberKey: activeMember?.key,
        initialFieldKey,
    });

    const removeChip = (chip: ChipGroupItem): void => {
        if (activeMember) {
            updateDraft(vm.removeValue(activeMember.key, draft, chip.type));
        }
    };

    const handleInclusionChange = (nextInclusion: InclusionOption): void => {
        if (selectedBaseKey) {
            const movedDraft = vm.moveDraftValue(selectedBaseKey, inclusion, nextInclusion, draft);

            if (movedDraft) {
                setDraft(movedDraft);
            }
        }

        setInclusion(nextInclusion);
    };

    const hasActiveValue = activeMember ? !activeMember.filter.isValueEmpty(draft[activeMember.key]) : false;

    const handleApply = (): void => {
        onApply(activeMember ? vm.commitActiveMember(activeMember.key, draft, initialFieldKey) : value);
    };

    const title: ReactNode = is.string(filter.title) ? filter.title : filter.title ? translate(filter.title.key) : null;

    const description = translate(advancedFilteringI18nKeys.groupDialog.emptyDescription);

    return (
        <FilterConditionDialog
            isOpen={isOpen}
            onRequestClose={onClose}
            dataAutomation="dynamic-group-modal"
            title={title}
            description={description}
            onCancel={onClose}
            onApply={handleApply}
            isApplyDisabled={!hasActiveValue}
        >
            <StyledDialogContent>
                <InclusionGroup
                    value={inclusion}
                    onChange={handleInclusionChange}
                />

                {chipItems.length > 0 ? (
                    <ChipsGroup
                        chipItems={chipItems}
                        divider={operatorOptionMap.or}
                        onDelete={removeChip}
                    />
                ) : null}

                <FormFieldLabel text={translate(advancedFilteringI18nKeys.groupDialog.filterLabel)}>
                    <Combobox
                        data-automation="dynamic-group-field-picker"
                        data-react-aria-top-layer="true"
                        placement="bottom-start"
                        isOpen={isPickerOpen}
                        onOpenChange={setIsPickerOpen}
                    >
                        <Combobox.Trigger>
                            <StyledBaseDropdownButtonWrap>
                                <DropdownButton hasValue={Boolean(selectedBaseKey)}>
                                    {selectedBaseKey
                                        ? vm.baseFields.find((field) => field.key === selectedBaseKey)?.label
                                        : translate(advancedFilteringI18nKeys.groupDialog.fieldPlaceholder)}
                                </DropdownButton>
                            </StyledBaseDropdownButtonWrap>
                        </Combobox.Trigger>

                        <Combobox.Content>
                            {vm.baseFields.map((field) => (
                                <Combobox.Option
                                    key={field.key}
                                    id={field.key}
                                    textValue={field.label}
                                    isSelected={field.key === selectedBaseKey}
                                    onClick={() => selectField(field.key)}
                                >
                                    <Combobox.OptionLabel>{field.label}</Combobox.OptionLabel>
                                </Combobox.Option>
                            ))}
                        </Combobox.Content>
                    </Combobox>
                </FormFieldLabel>

                {activeMember ? (
                    <GroupField
                        group={filter}
                        field={activeMember}
                        groupValue={draft}
                        disabledOptionIds={disabledOptionIds}
                        isTopLayer
                        view={activeMember.filterView}
                        onFilterUpdate={updateDraft}
                    />
                ) : null}
            </StyledDialogContent>
        </FilterConditionDialog>
    );
};
